/**
 * The shared Web Audio context (docs/architecture.md §10, §17). Browsers, iOS Safari above all,
 * only start audio inside a user gesture, so the context is created or resumed on a tap or key
 * press and then kept for the whole visit: later fights can play their opening bell at once.
 */
let ctx: AudioContext | null = null;

export function audioContext(): AudioContext | null {
  return ctx;
}

/** Whether the page has had a user gesture, so audio and vibration are allowed to start. */
export function hasUserActivation(): boolean {
  const activation = (navigator as Navigator & { userActivation?: UserActivation }).userActivation;
  return activation?.hasBeenActive ?? false;
}

/** Creates or resumes the context. Call it from a pointerdown or keydown handler (iOS unlock). */
export function unlockAudio(): AudioContext | null {
  if (!ctx) {
    if (typeof AudioContext !== 'function') return null;
    try {
      ctx = new AudioContext({ latencyHint: 'interactive' });
    } catch {
      return null;
    }
  }
  if (ctx.state !== 'running') {
    void ctx.resume().catch(() => {});
    // iOS only unlocks output once something plays inside the gesture: one silent sample.
    try {
      const src = ctx.createBufferSource();
      src.buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
      src.connect(ctx.destination);
      src.start();
    } catch {
      // ignore
    }
  }
  return ctx;
}
